import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, X, Shield, Copy, Trash2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useDriveAuth } from "../context/DriveAuthContext";

const ROLES = [
  { value: "superadmin", label: "Superadmin", color: "text-[#ff0033]" },
  { value: "staff", label: "Mitarbeiter", color: "text-white" },
  { value: "artist", label: "Künstler", color: "text-emerald-400" },
  { value: "customer", label: "Kunde", color: "text-blue-400" },
];

const PERMISSIONS = [
  { key: "files_read",     label: "Dateien: Lesen" },
  { key: "files_write",    label: "Dateien: Schreiben" },
  { key: "crm_read",       label: "CRM: Lesen" },
  { key: "crm_write",      label: "CRM: Schreiben" },
  { key: "contracts_read", label: "Verträge: Lesen" },
  { key: "contracts_write",label: "Verträge: Schreiben" },
  { key: "settings",       label: "Einstellungen" },
];

export default function DriveSettings() {
  const { profile: myProfile } = useDriveAuth();
  const [rows, setRows] = useState([]);
  const [artists, setArtists] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [permOpen, setPermOpen] = useState(null); // profile row currently editing permissions

  const load = async () => {
    setLoading(true);
    try {
      const [profs, arts, custs] = await Promise.all([
        supabase.from("drive_profiles").select("id, role, display_name, artist_id, customer_id, permissions, created_at").order("created_at", { ascending: false }),
        supabase.from("drive_artists").select("id, artist_name"),
        supabase.from("drive_customers").select("id, name"),
      ]);
      if (profs.error) throw profs.error;
      setRows(profs.data);
      setArtists(arts.data || []);
      setCustomers(custs.data || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      const { error } = await supabase.from("drive_profiles").update(patch).eq("id", id);
      if (error) throw error;
      toast.success("Aktualisiert");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteProfile = async (id, name) => {
    if (id === myProfile?.id) { toast.error("Du kannst dich nicht selbst löschen"); return; }
    if (!window.confirm(`Nutzer „${name || id}" wirklich entfernen?\n\nDamit wird nur das Profil entfernt — der Login-Account bleibt bestehen und muss zusätzlich in Supabase Auth deaktiviert werden.`)) return;
    try {
      const { error } = await supabase.from("drive_profiles").delete().eq("id", id);
      if (error) throw error;
      toast.success("Profil entfernt");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
            <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
            Benutzerverwaltung
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-none text-3xl md:text-4xl">Rollen & Rechte</h1>
          <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
            Neue Zugänge erstellen, Rollen setzen und Berechtigungen fein-granular pro Nutzer definieren.
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          data-testid="btn-invite-user"
          className="inline-flex items-center gap-2 px-5 py-3 text-[10px] font-bold tracking-[0.25em] uppercase bg-white text-black hover:bg-[#ff0033] hover:text-white transition-colors"
        >
          <UserPlus size={13} /> Neuen Zugang erstellen
        </button>
      </motion.div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500">Lade…</div>
      ) : (
        <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
          {rows.map((r) => (
            <div key={r.id} data-testid={`user-${r.id}`} className="p-4 md:px-6 grid md:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 items-center">
              <div>
                <div className="text-sm text-white font-medium">
                  {r.display_name || <span className="text-zinc-500">Ohne Namen</span>}
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-0.5 font-mono truncate max-w-md">
                  {r.id === myProfile?.id ? "DU · " : ""}
                  {r.id}
                </div>
              </div>

              <select
                value={r.role}
                onChange={(e) => update(r.id, { role: e.target.value })}
                data-testid={`role-${r.id}`}
                disabled={r.id === myProfile?.id}
                className="bg-transparent border border-white/15 text-white text-xs uppercase tracking-wider px-3 py-2 outline-none focus:border-[#ff0033]/50 disabled:opacity-50"
              >
                {ROLES.map((o) => (
                  <option key={o.value} value={o.value} className="bg-[#0A0A0A]">{o.label}</option>
                ))}
              </select>

              <select
                value={r.artist_id || ""}
                onChange={(e) => update(r.id, { artist_id: e.target.value || null })}
                data-testid={`artist-link-${r.id}`}
                className="bg-transparent border border-white/15 text-white text-xs uppercase tracking-wider px-3 py-2 outline-none focus:border-[#ff0033]/50 max-w-[180px]"
              >
                <option value="" className="bg-[#0A0A0A]">— Kein Künstler —</option>
                {artists.map((a) => (
                  <option key={a.id} value={a.id} className="bg-[#0A0A0A]">{a.artist_name}</option>
                ))}
              </select>

              <select
                value={r.customer_id || ""}
                onChange={(e) => update(r.id, { customer_id: e.target.value || null })}
                data-testid={`customer-link-${r.id}`}
                className="bg-transparent border border-white/15 text-white text-xs uppercase tracking-wider px-3 py-2 outline-none focus:border-[#ff0033]/50 max-w-[180px]"
              >
                <option value="" className="bg-[#0A0A0A]">— Kein Kunde —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.name}</option>
                ))}
              </select>

              <button
                onClick={() => setPermOpen(r)}
                data-testid={`perms-${r.id}`}
                title="Berechtigungen"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors"
              >
                <Shield size={11} /> Rechte
              </button>

              {r.id !== myProfile?.id && (
                <button
                  onClick={() => deleteProfile(r.id, r.display_name)}
                  data-testid={`delete-user-${r.id}`}
                  title="Profil löschen"
                  className="p-2 text-zinc-500 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.08]"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onDone={load} />
      )}

      {permOpen && (
        <PermissionsModal
          profile={permOpen}
          onClose={() => setPermOpen(null)}
          onSaved={async () => { setPermOpen(null); await load(); }}
        />
      )}
    </div>
  );
}

// ═══ Invite Modal — creates auth user + drive_profiles row ═══
const InviteModal = ({ onClose, onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("E-Mail und Passwort sind Pflicht"); return; }
    if (password.length < 8) { toast.error("Passwort mindestens 8 Zeichen"); return; }
    setBusy(true);

    // Aktuelle Session sichern (damit Admin nach signUp zurück-eingeloggt werden kann)
    const { data: currentSession } = await supabase.auth.getSession();
    const adminEmail = currentSession?.session?.user?.email;

    try {
      // 1) Nutzer via signUp anlegen
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: displayName || email.split("@")[0] } },
      });
      if (signUpErr) throw signUpErr;
      const newUserId = signUpData?.user?.id;

      // 2) Sofort wieder ausloggen und Admin einloggen
      await supabase.auth.signOut();

      // 3) Rolle + Display-Name im drive_profiles setzen (Trigger legt Row an; wir überschreiben)
      // Das geht erst wenn Admin wieder eingeloggt ist — also erst nach Re-Login
      // Deshalb: Zeige Admin die Credentials + Bitte um Re-Login
      setResult({
        newUserId,
        email,
        password,
        displayName: displayName || email.split("@")[0],
        role,
        adminEmail,
      });
      toast.success("Nutzer angelegt — jetzt bitte mit deinem Admin-Passwort neu einloggen.");
    } catch (err) {
      toast.error(err.message || "Fehler beim Anlegen");
      setBusy(false);
    }
  };

  const copyCreds = async () => {
    const txt = `Straßenwert Drive Zugang:\nURL: ${window.location.origin}/drive/login\nE-Mail: ${result.email}\nPasswort: ${result.password}`;
    await navigator.clipboard.writeText(txt);
    toast.success("Zugangsdaten in Zwischenablage kopiert");
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#0A0A0A] border border-[#ff0033]/40 max-w-lg w-full p-6 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-1">Nutzer angelegt ✓</div>
            <div className="font-display font-black uppercase text-xl">Nächste Schritte</div>
          </div>
          <div className="bg-[#0F0F0F] border border-white/10 p-4 text-sm text-zinc-300 space-y-2 font-mono">
            <div><span className="text-zinc-500">E-Mail:</span> {result.email}</div>
            <div><span className="text-zinc-500">Passwort:</span> {result.password}</div>
            <div><span className="text-zinc-500">Rolle:</span> {result.role}</div>
          </div>
          <ol className="text-xs text-zinc-400 space-y-1.5 list-decimal ml-4">
            <li>Kopiere die Zugangsdaten und sende sie an den neuen Nutzer.</li>
            <li>Klicke unten auf <strong className="text-white">Zur Login-Seite</strong> und melde dich mit deinem <strong className="text-white">Admin-Passwort</strong> neu an.</li>
            <li>In der Rollenverwaltung findest du dann den neuen Nutzer und kannst Rolle & Berechtigungen anpassen (Rolle bereits auf {`„${result.role}"`} vorgeschlagen — Änderung nach Re-Login möglich).</li>
          </ol>
          <div className="flex items-center gap-2 pt-2 border-t border-white/[0.06]">
            <button onClick={copyCreds} data-testid="copy-creds" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase">
              <Copy size={12} /> Zugangsdaten kopieren
            </button>
            <a
              href="/drive/login"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#ff0033] hover:text-white px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase"
            >
              Zur Login-Seite →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-black uppercase text-lg">Neuen Zugang erstellen</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Anzeigename">
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="inv-name" placeholder="Max Mustermann" className="form-input" />
          </Field>
          <Field label="E-Mail *">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="inv-email" placeholder="name@email.com" className="form-input" />
          </Field>
          <Field label="Passwort * (min. 8 Zeichen)">
            <input required type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="inv-password" placeholder="Wird an den Nutzer weitergegeben" className="form-input" />
          </Field>
          <Field label="Rolle (nach Re-Login final zuweisen)">
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="inv-role" className="form-input">
              {ROLES.map((o) => <option key={o.value} value={o.value} className="bg-[#0A0A0A]">{o.label}</option>)}
            </select>
          </Field>
          <div className="bg-[#ff0033]/10 border border-[#ff0033]/30 p-3 text-xs text-zinc-300">
            <strong className="text-[#ff0033]">Wichtig:</strong> Nach dem Erstellen wirst du automatisch ausgeloggt. Du musst dich anschließend mit deinem Admin-Passwort neu einloggen — das ist eine Sicherheits-Maßnahme von Supabase.
          </div>
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/[0.06]">
            <button type="button" onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white">Abbrechen</button>
            <button type="submit" disabled={busy} data-testid="inv-submit" className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase disabled:opacity-50">
              {busy ? "Erstelle…" : "Zugang erstellen"}
            </button>
          </div>
        </form>
        <style>{`
          .form-input { width:100%; background:transparent; border:0; border-bottom:1px solid rgba(255,255,255,0.12); padding:10px 0; color:#fff; font-size:14px; outline:none; }
          .form-input:focus { border-color: rgba(255,0,51,0.6); }
        `}</style>
      </div>
    </div>
  );
};

// ═══ Permissions Modal — fine-grained toggles ═══
const PermissionsModal = ({ profile, onClose, onSaved }) => {
  const [perms, setPerms] = useState(profile.permissions || {});
  const [busy, setBusy] = useState(false);

  const toggle = (key) => setPerms((p) => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.from("drive_profiles").update({ permissions: perms }).eq("id", profile.id);
      if (error) throw error;
      toast.success("Berechtigungen gespeichert");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-1">Rechte</div>
            <div className="font-display font-black uppercase text-lg truncate">{profile.display_name || profile.id.slice(0, 8)}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="space-y-2">
          {PERMISSIONS.map((p) => (
            <label key={p.key} className="flex items-center justify-between gap-3 p-3 bg-[#0F0F0F] border border-white/[0.06] cursor-pointer hover:border-[#ff0033]/30">
              <span className="text-sm text-white">{p.label}</span>
              <input
                type="checkbox"
                checked={!!perms[p.key]}
                onChange={() => toggle(p.key)}
                data-testid={`perm-${p.key}`}
                className="w-5 h-5 accent-[#ff0033]"
              />
            </label>
          ))}
        </div>
        <div className="text-xs text-zinc-500 leading-relaxed">
          Hinweis: Superadmins haben unabhängig davon vollen Zugriff. Die Rolle {`„Mitarbeiter"`} bekommt via SQL-Policies vollen Datenzugriff — die Toggles hier steuern das UI (welche Bereiche angezeigt werden).
        </div>
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/[0.06]">
          <button onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white">Abbrechen</button>
          <button onClick={save} disabled={busy} data-testid="perms-save" className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase disabled:opacity-50">
            {busy ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">{label}</span>
    {children}
  </label>
);
